import type { Cell, Row, Worksheet } from "exceljs";
import ExcelJS from "exceljs";
import type { DateTime } from "luxon";
import { fetchPtoDeletedShifts } from "../components/staffing/downloadScheduleUtils";
import type { Department } from "../contexts/AuthProvider";
import type { PrintoutColumn, PrintoutColumnType } from "../pages/DaySchedule/Header/usePrintout";
import { trackErrorEvent, trackEvent } from "../utils/analytics";
import { applyStyleOverlayToWorkbook, type SheetStyleOverlay } from "../utils/fortuneSheetStyleOverlay";
import {
	type CalledOffShift,
	type CensusPointInTimes,
	type CustomColumnNamesConfig,
	type CustomSheetTabsConfig,
	type CustomTimeGroupsConfig,
	type DailyPrintoutGrouping,
	DailyPrintoutGroupingOption,
	type DayStats,
	type HospitalData,
	type NurseInShift,
	type PrintoutOrientation,
	type PrintoutShowAdminsConfig,
} from "../utils/models";
import { createDetailedDayView, type ShiftTimeAnchor } from "./dailyPrintout";
import { createDayViewLowry } from "./downloadDayViewLowry";
import { applyWrapText } from "./excelWrapText";

const NIGHT_SHIFT_PATTERN = /\d+pm.*\d+am/i;

export function addTitle(worksheet: Worksheet, start_cell: string, end_cell: string, title: string) {
	worksheet.mergeCells(`${start_cell}:${end_cell}`);
	const titleCell = worksheet.getCell(start_cell);
	titleCell.value = title;
	titleCell.alignment = { horizontal: "center" };
	titleCell.font = { bold: true, size: 24 };
}

export function addBorders(worksheet: Worksheet) {
	worksheet.eachRow((row: Row) => {
		row.eachCell((cell: Cell) => {
			cell.border = {
				top: { style: "thin" },
				left: { style: "thin" },
				bottom: { style: "thin" },
				right: { style: "thin" },
			};
		});
	});
}

export function addHorizontalDividers(worksheet: Worksheet, blackRow: number, col_amount: number) {
	// Set row height to be narrow
	worksheet.getRow(blackRow).height = 2;

	// Make entire row black
	const cols = Array.from({ length: col_amount }, (_, i) => String.fromCharCode("A".charCodeAt(0) + i));
	cols.forEach((col) => {
		const cell = worksheet.getCell(`${col}${blackRow}`);

		// Fill cell with black color
		cell.fill = {
			type: "pattern",
			pattern: "solid",
			fgColor: { argb: "000000" }, // Black fill
		};

		// Optional: Add black borders to ensure no white gaps
		cell.border = {
			top: { style: "thick", color: { argb: "000000" } },
			bottom: { style: "thick", color: { argb: "000000" } },
			left: { style: "thick", color: { argb: "000000" } },
			right: { style: "thick", color: { argb: "000000" } },
		};
	});
}

export function addStaffingSummaryTableFloors(worksheet: Worksheet, row: number, shiftToNurseMap: any) {
	// Sample data rows (replace with actual data calculation)
	// Generate staffing data from shiftToNurseMap
	const staffingData: {
		role: string;
		count: number | string;
		hours: number | string;
	}[] = [];

	const floorKeys = Object.keys(shiftToNurseMap);
	// Group nurses by role and shift time
	const roleGroups: Record<string, { role: string; count: number; hours: number }> = {};
	for (const floorKey of floorKeys) {
		const floorShiftData = shiftToNurseMap[floorKey];
		if (!floorShiftData) continue;
		// Process day shift (index 0) and night shift (index 1)
		for (let shiftIndex = 0; shiftIndex < 2; shiftIndex++) {
			const shiftData = floorShiftData[shiftIndex];
			if (!shiftData) continue;

			for (const key in shiftData) {
				const nurseData = shiftData[key][0];
				if (key !== "census") {
					// Check if location contains nurse type, otherwise use location and shift
					let roleKey: string;
					if (nurseData.role === undefined || nurseData.shift === undefined) {
						continue;
					}
					roleKey = `${nurseData.role} ${nurseData.shift}`;
					if (!roleGroups[roleKey]) {
						roleGroups[roleKey] = {
							role: roleKey,
							count: 0,
							hours: 0,
						};
					}

					roleGroups[roleKey].count += 1;
					roleGroups[roleKey].hours += nurseData.hours || 0;
				}
			}
		}
	}
	// Add grouped data to staffingData
	for (const group of Object.values(roleGroups)) {
		// Format hours to have at most 1 decimal place if not an integer
		group.hours = Number.isInteger(group.hours) ? group.hours : Number(group.hours.toFixed(1));

		staffingData.push(group);
	}

	// Sort staffing data: day shift first, then night shift
	staffingData.sort((a, b) => {
		// Check if role contains night shift indicators (pm to am pattern)
		const aIsNight = NIGHT_SHIFT_PATTERN.test(a.role);
		const bIsNight = NIGHT_SHIFT_PATTERN.test(b.role);

		// Put night shifts at the end
		if (aIsNight && !bIsNight) return 1;
		if (!aIsNight && bIsNight) return -1;

		// If both are same shift type (day or night), sort alphabetically
		return a.role.localeCompare(b.role);
	});

	// Insert two empty rows between day and night shifts
	const firstNightShiftIndex = staffingData.findIndex((item) => NIGHT_SHIFT_PATTERN.test(item.role));
	if (firstNightShiftIndex > 0) {
		// Insert two empty placeholder rows
		staffingData.splice(firstNightShiftIndex, 0, { role: "", count: "", hours: "" }, { role: "", count: "", hours: "" });
	}

	// Add data rows
	staffingData.forEach((item, index) => {
		const currentRow = row + index + 1;
		worksheet.getCell(`A${currentRow}`).value = item.role;
		worksheet.getCell(`B${currentRow}`).value = item.count;
		worksheet.getCell(`C${currentRow}`).value = item.hours;
	});

	// Set column widths for the summary table
	worksheet.getColumn("A").width = 25;
	worksheet.getColumn("B").width = 10;
	worksheet.getColumn("C").width = 10;
}

export async function createDailySchedule({
	hospitalData,
	weekIndex,
	dayIndex,
	department,
	day,
	date,
	isLowrySpreadsheet,
	summary,
	printNotes,
	printGaps,
	showRoleGroupStats,
	dayStats,
	printoutGroupBy,
	selectedLocations,
	availableLocations,
	divideGroupsByPage,
	userId,
	separateSheetsBy,
	customSheetTabs,
	showAdmins,
	showLocationCensus,
	showPtoSheet,
	callOutShifts,
	includeTimeOffReason,
	columns,
	sortBy,
	staffPerRow,
	rowGroupingBy,
	columnGroupingBy,
	spaceBetweenGroups,
	orientation,
	fitToPage,
	fitToWidth,
	fitToHeight,
	hideHppdNursingHours,
	printoutColumnWidths,
	nurses,
	flagNotOnboarded,
	flagAgency,
	mergeSimilarShiftTimes,
	alignGroupTimesToPopular,
	customTimeGroups,
	customTimeGroupsOtherLabel,
	appendRoleToName,
	customColumnNames,
	showDhppd,
	showNhppd,
	showActualStaffColumn,
	showActualHoursColumn,
	dhppd,
	nhppd,
	shiftTimeAnchors,
	customStringOrderMap,
	jobManagementEnabled,
	lastNameFirst,
	censusPointInTimes,
	styleOverlay,
}: {
	hospitalData: HospitalData;
	weekIndex: number;
	dayIndex: number;
	department: Department;
	day: string;
	date: DateTime;
	isLowrySpreadsheet?: boolean;
	summary: boolean;
	printNotes: boolean;
	printGaps: boolean;
	showRoleGroupStats?: boolean;
	dayStats?: DayStats;
	printoutGroupBy: DailyPrintoutGrouping;
	selectedLocations: string[];
	availableLocations: string[];
	divideGroupsByPage?: boolean;
	userId: string;
	separateSheetsBy?: DailyPrintoutGroupingOption;
	customSheetTabs?: CustomSheetTabsConfig;
	showAdmins?: PrintoutShowAdminsConfig;
	showLocationCensus?: boolean;
	showPtoSheet?: boolean;
	callOutShifts: CalledOffShift[];
	includeTimeOffReason?: boolean;
	columns?: PrintoutColumn[];
	sortBy?: PrintoutColumn[];
	staffPerRow?: number;
	rowGroupingBy?: PrintoutColumnType[];
	columnGroupingBy?: PrintoutColumnType[] | null;
	spaceBetweenGroups?: boolean;
	orientation?: PrintoutOrientation;
	fitToPage?: boolean;
	fitToWidth?: number;
	fitToHeight?: number;
	hideHppdNursingHours?: boolean;
	printoutColumnWidths?: Record<string, number>;
	nurses: NurseInShift[];
	flagNotOnboarded?: boolean;
	flagAgency?: boolean;
	mergeSimilarShiftTimes?: boolean;
	alignGroupTimesToPopular?: boolean;
	customTimeGroups: CustomTimeGroupsConfig;
	customTimeGroupsOtherLabel: string;
	appendRoleToName: boolean;
	customColumnNames: CustomColumnNamesConfig;
	showDhppd: boolean;
	showNhppd: boolean;
	showActualStaffColumn: boolean;
	showActualHoursColumn: boolean;
	dhppd: number;
	nhppd: number;
	shiftTimeAnchors?: ShiftTimeAnchor[];
	customStringOrderMap?: Map<string, number> | null;
	jobManagementEnabled: boolean;
	/** The department's `name_format_last_first` flag (`useLastNameFirst()`). */
	lastNameFirst: boolean;
	censusPointInTimes: CensusPointInTimes | undefined;
	/** Session-only cell formatting the user applied in the printout preview. Absent everywhere else. */
	styleOverlay?: SheetStyleOverlay;
}) {
	const workbook = new ExcelJS.Workbook();

	trackEvent("download_day_view", {
		type: summary ? "summary" : "detailed",
		date: date,
		department: department.department_id,
	});
	try {
		// Fetch PTO-deleted shifts if showPtoSheet is enabled
		const dateStr = date.toFormat("yyyy-MM-dd");
		// The PTO sheet is an optional extra: `fetchPtoDeletedShifts` throws so the schedule export can
		// refuse to hand over a file it claims contains PTO, but here the day's schedule is the point and
		// losing all of it because one supplementary sheet failed is worse. Reported, never silent.
		let ptoDeletedShifts: NurseInShift[] = [];
		if (showPtoSheet && !isLowrySpreadsheet && !summary) {
			try {
				ptoDeletedShifts = await fetchPtoDeletedShifts(department.department_id, dateStr, dateStr, hospitalData.nurses, lastNameFirst);
			} catch (error) {
				trackErrorEvent("download_day_view_pto_fetch_error", error, { date: date, department: department.department_id });
			}
		}

		if (isLowrySpreadsheet) {
			const worksheet: Worksheet = workbook.addWorksheet("Daily Schedule");
			await createDayViewLowry(worksheet, hospitalData, weekIndex, dayIndex, department, day, date, summary, lastNameFirst, jobManagementEnabled);
		} else {
			await createDetailedDayView({
				workbook,
				hospitalData,
				weekIndex,
				dayIndex,
				department,
				day,
				date,
				summary,
				dayStats: dayStats!,
				printNotes,
				printGaps,
				showRoleGroupStats: showRoleGroupStats ?? false,
				printoutGroupBy,
				selectedLocations,
				availableLocations,
				nurses,
				divideGroupsByPage: divideGroupsByPage ?? false,
				userId,
				separateSheetsBy: separateSheetsBy ?? DailyPrintoutGroupingOption.NONE,
				customSheetTabs,
				showAdmins,
				showLocationCensus,
				ptoDeletedShifts,
				callOutShifts,
				columns,
				sortBy,
				staffPerRow,
				rowGroupingBy,
				columnGroupingBy: columnGroupingBy?.[0] ?? null,
				spaceBetweenGroups,
				includeTimeOffReason,
				orientation,
				fitToPage,
				fitToWidth,
				fitToHeight,
				hideHppdNursingHours,
				printoutColumnWidths,
				flagNotOnboarded,
				flagAgency,
				mergeSimilarShiftTimes,
				alignGroupTimesToPopular,
				customTimeGroups,
				customTimeGroupsOtherLabel,
				appendRoleToName,
				customColumnNames,
				showDhppd,
				showNhppd,
				showActualStaffColumn,
				showActualHoursColumn,
				dhppd,
				nhppd,
				shiftTimeAnchors,
				customStringOrderMap,
				jobManagementEnabled,
				lastNameFirst,
				censusPointInTimes,
			});
		}

		// BEFORE `applyWrapText`, and the ordering is deliberate: the overlay writes cell alignments, and
		// `applyWrapText`'s own header states it must run after everything else so that no later write
		// drops `wrapText` off an alignment it replaces. Every overlay key is paint — `fs` is excluded, see
		// `OVERLAY_STYLE_KEYS` — so nothing here can change a row height and move a page boundary.
		if (styleOverlay) applyStyleOverlayToWorkbook(workbook, styleOverlay);

		// Wrap EVERY cell of every sheet, and size each row to what it wrapped to.
		//
		// This used to set `wrapText` on the column style instead, which silently skipped every cell
		// that writes its own `alignment` — section headers, the centered stat cells, the vertical
		// merges — because a cell-level alignment replaces the column's wholesale. Doing it per cell
		// after the renderers have run is what makes "all cells" true, and is also the only chance to
		// give the row the height the extra lines need (the preview and the PDF cannot auto-fit).
		workbook.eachSheet((worksheet) => applyWrapText(worksheet));

		const buffer = await workbook.xlsx.writeBuffer();
		trackEvent("download_day_view_success", {
			type: summary ? "summary" : "detailed",
			date: date,
			department: department.department_id,
		});
		return buffer;
	} catch (error) {
		trackErrorEvent("download_day_view_error", error, {
			type: summary ? "summary" : "detailed",
			date: date,
			department: department.department_id,
		});
		throw error;
	}
}
